/* 레이스 경기 스케줄 — 공통 규칙

   겹침 판정은 서버(runningtrack.ctrl.js)에도 같은 규칙으로 들어가 있다.
   화면에서 막는 것은 편의이고 정본 판정은 서버다. 두 곳을 함께 고쳐야 한다.
   상세: docs/race/제타트랙_레이스모드_작업계획서.md §5.2
*/

export const REPEAT_WEEKLY = 0;
export const REPEAT_DAILY = 1;
export const REPEAT_ONCE = 2;

export const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];
export const ALL_DAYS_MASK = 0x7f;

export const MIN_M = 200;
export const MAX_M = 10000;
export const STEP_M = 100;

//--> 자주 쓰는 거리. 표준 거리라는 뜻은 없고 입력 단축키일 뿐이다.
export const QUICK_DISTANCES = [1000, 1600, 2200, 3000, 4000];

/* 경기 종료 시간은 100m 당 1분으로 자동 계산한다.
   거리를 바꾸면 항상 다시 계산해 덮어쓴다 — 거리만 고치고 종료 시간을 그대로 두면
   둘이 조용히 어긋나기 때문이다. */
export const autoEndMin = (meters) => Math.max(1, Math.round((Number(meters) || 0) / 100));

/* 이 거리를 가장 빨리(16km/h) 달렸을 때 걸리는 시간.
   종료 시간이 이보다 짧으면 아무도 완주할 수 없으므로 저장을 막는다. */
export const minFinishMin = (meters) =>
  Math.max(1, Math.ceil((Number(meters) || 0) / (16 * 1000) * 60));

export const secToLabel = (sec) => {
  const s = ((Number(sec) || 0) % 86400 + 86400) % 86400;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return ('0' + h).slice(-2) + ':' + ('0' + m).slice(-2);
};

export const labelToSec = (label) => {
  const p = String(label || '0:0').split(':');
  return (Number(p[0]) || 0) * 3600 + (Number(p[1]) || 0) * 60;
};

export const addMinutesLabel = (startSec, min) => secToLabel(Number(startSec) + Number(min) * 60);

export const ymdToDow = (ymd) => {
  const s = String(ymd || '');
  if (s.length !== 8) return -1;
  return new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8))).getDay();
};

//--> 이 경기가 도는 요일 비트마스크
export const dayMask = (race) => {
  const type = Number(race.repeatType);
  if (type === REPEAT_DAILY) return ALL_DAYS_MASK;
  if (type === REPEAT_ONCE) {
    const dow = ymdToDow(race.onceDate);
    return dow < 0 ? 0 : (1 << dow);
  }
  return Number(race.repeatDays) & ALL_DAYS_MASK;
};

export const maskToDays = (mask) => {
  const out = [];
  for (let d = 0; d < 7; d++) if (mask & (1 << d)) out.push(d);
  return out;
};

export const daysToMask = (days) => days.reduce((a, d) => a | (1 << d), 0);

//--> 반복을 사람이 읽는 말로. 평일/주말은 자주 나오는 조합이라 따로 이름을 준다.
export const repeatLabel = (race) => {
  const type = Number(race.repeatType);
  if (type === REPEAT_DAILY) return '매일';
  if (type === REPEAT_ONCE) {
    const s = String(race.onceDate || '');
    return s.length === 8 ? `${Number(s.slice(4, 6))}월 ${Number(s.slice(6, 8))}일` : '1회';
  }
  const mask = Number(race.repeatDays) & ALL_DAYS_MASK;
  if (mask === ALL_DAYS_MASK) return '매일';
  if (mask === 0b0111110) return '평일';
  if (mask === 0b1000001) return '주말';
  return maskToDays(mask).map((d) => DAY_NAMES[d]).join(' · ');
};

/* 요일별 [시작,끝) 구간. 자정을 넘는 경기는 다음 요일 구간으로 쪼갠다. */
export const segments = (race) => {
  const mask = dayMask(race);
  const start = Number(race.startTime) || 0;
  const end = start + (Number(race.endMin) || 0) * 60;
  const segs = [];
  for (let d = 0; d < 7; d++) {
    if (!(mask & (1 << d))) continue;
    if (end <= 86400) {
      segs.push({ d: d, s: start, e: end });
    } else {
      segs.push({ d: d, s: start, e: 86400 });
      segs.push({ d: (d + 1) % 7, s: 0, e: end - 86400 });
    }
  }
  return segs;
};

/* 겹치는 경기를 찾는다. 없으면 null.
   - 꺼둔 경기는 돌지 않으므로 검사 대상이 아니다
   - 수정 중인 경기는 자기 자신과 겹친다고 하면 안 되므로 excludeID 로 뺀다
   - 끝나는 시각에 시작하는 경기는 겹치지 않는다 (반열린 구간) */
export const findConflict = (candidate, schedules, excludeID) => {
  const mine = segments(candidate);
  for (const row of schedules || []) {
    if (Number(row.active) === 0) continue;
    if (excludeID && row.clubRaceScheduleID === excludeID) continue;
    const segs = segments(row);
    for (const a of mine) {
      for (const b of segs) {
        if (a.d === b.d && a.s < b.e && b.s < a.e) return row;
      }
    }
  }
  return null;
};

//--> 저장 전 검증. 서버와 같은 순서·같은 문구로 둔다.
export const validateRace = (r) => {
  const distance = Number(r.distance) || 0;
  if (distance < MIN_M || distance > MAX_M) {
    return `거리는 ${MIN_M}m 이상 ${MAX_M.toLocaleString()}m 이하로 입력하세요.`;
  }
  if (distance % STEP_M !== 0) return '거리는 100m 단위로 입력하세요.';

  const floor = minFinishMin(distance);
  if ((Number(r.endMin) || 0) < floor) {
    return `경기 종료 시간이 너무 짧습니다. ${distance.toLocaleString()}m 는 가장 빨리 달려도 ${floor}분이 걸립니다.`;
  }
  if (Number(r.repeatType) === REPEAT_WEEKLY && (Number(r.repeatDays) & ALL_DAYS_MASK) === 0) {
    return '반복할 요일을 하나 이상 선택하세요.';
  }
  if (Number(r.repeatType) === REPEAT_ONCE && String(r.onceDate || '').length !== 8) {
    return '경기 날짜를 선택하세요.';
  }
  const p1 = Number(r.prize1) || 0, p2 = Number(r.prize2) || 0, p3 = Number(r.prize3) || 0;
  if (!(p1 >= p2 && p2 >= p3)) return '상금은 1등 ≥ 2등 ≥ 3등 순서여야 합니다.';
  if ((Number(r.minMember) || 0) < 1) return '최소 참여 인원은 1명 이상이어야 합니다.';
  return null;
};

export const dateToYmd = (d) =>
  d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();

/* 표시 중인 주의 7일. 월요일 시작. offset 0 이 이번 주다. */
export const weekDates = (offset) => {
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  //--> getDay() 는 일=0 이라 월요일까지 되감는 양이 요일마다 다르다
  const backToMonday = (base.getDay() + 6) % 7;
  base.setDate(base.getDate() - backToMonday + (Number(offset) || 0) * 7);
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    out.push(d);
  }
  return out;
};

/* 이 경기가 그 날짜에 실제로 열리는가.

   1회성 경기를 요일로만 보면 매주 반복되는 것처럼 보인다. 날짜까지 봐야 한다. */
export const occursOn = (race, date) => {
  if (Number(race.active) === 0) return false;
  const type = Number(race.repeatType);
  if (type === REPEAT_ONCE) return Number(race.onceDate) === dateToYmd(date);
  if (type === REPEAT_DAILY) return true;
  return !!(Number(race.repeatDays) & (1 << date.getDay()));
};

/* 표시 중인 주의 지표 — 화면에 실제로 그려진 것과 같은 수를 낸다.
   dates 를 주지 않으면 이번 주 기준이다. */
export const weeklyStats = (schedules, dates) => {
  const week = dates || weekDates(0);
  let count = 0;
  let point = 0;
  for (const row of schedules || []) {
    const times = week.filter((d) => occursOn(row, d)).length;
    if (times === 0) continue;
    const sum = (Number(row.prize1) || 0) + (Number(row.prize2) || 0) + (Number(row.prize3) || 0);
    count += times;
    point += times * sum;
  }
  return { count, point };
};
